import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useQueryClient } from "react-query";
import { invalidateTools, nextStock, saveStock } from "./toolsData";

export default function StockDialog({ product, onClose, onSaved }) {
  const [mode, setMode] = useState("add"),
    [amount, setAmount] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const client = useQueryClient();
  let next = null;
  try {
    next = nextStock(product.stock, mode, amount);
  } catch (_) {}
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setError("");
    setSaving(true);
    try {
      const stock = await saveStock(product, mode, amount);
      await invalidateTools(client);
      setSaving(false);
      onSaved(`${product.name}: stock updated to ${stock} units.`);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Unable to update stock. Please try again.",
      );
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Update stock</DialogTitle>
        <DialogContent>
          <Typography fontWeight={800} sx={{ mb: 1 }}>
            {product.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Record incoming units, remove damaged stock, or correct your stock count.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            select
            fullWidth
            label="Stock operation"
            value={mode}
            onChange={(event) => {
              setMode(event.target.value);
              setError("");
            }}
            disabled={saving}
            sx={{ mb: 2 }}
          >
            <MenuItem value="add">Add received stock</MenuItem>
            <MenuItem value="remove">Remove stock</MenuItem>
            <MenuItem value="set">Set actual stock count</MenuItem>
          </TextField>
          <TextField
            autoFocus
            required
            fullWidth
            label={mode === "set" ? "Actual stock count" : "Number of units"}
            type="number"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setError("");
            }}
            inputProps={{ min: mode === "set" ? 0 : 1, step: 1 }}
            disabled={saving}
            helperText="Use whole units. Stock cannot be negative."
          />
          <Box
            sx={{
              my: 3,
              p: 2,
              bgcolor: "background.default",
              borderRadius: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              <Typography variant="caption">Current stock</Typography>
              <Typography variant="h6">{product.stock} units</Typography>
            </div>
            <div>
              <Typography variant="caption">Stock after update</Typography>
              <Typography variant="h6">{next === null ? "—" : `${next} units`}</Typography>
            </div>
          </Box>
          {mode === "set" && (
            <Alert severity="info">
              This replaces the stock quantity with your physical count.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={saving}
            variant="contained"
            disabled={next === null || next === Number(product.stock)}
          >
            Save stock
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
